import React, { useCallback, useContext, useRef } from 'react';
import cls from 'js/utils/classNameBuilder.util';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';

import { TabsContext } from './context';

import style from './index.scss';

export const scrollIntoTab = (container, target) => {
    const { right: cRight, left: cLeft } = container.getBoundingClientRect();
    const { right: tRight, left: tLeft } = target.getBoundingClientRect();

    if (cRight < tRight || cLeft > tLeft) {
        container.scrollLeft = target.offsetLeft;
    }
};

export const useTabClick = ({ isActive, tabsRef, value, onChange }) =>
    useCallback((event) => {
        if (isActive || !tabsRef.current) {
            return;
        }

        onChange(value);
        scrollIntoTab(tabsRef.current, event.currentTarget);
    }, [isActive, tabsRef, onChange, value]);

export const Tab = ({ className = '', children, value }) => {
    const { onChange, activeTab, classNames = {}, tabsRef } = useContext(TabsContext);
    const isActive = value === activeTab;
    const onClick = useTabClick({ isActive, value, onChange, tabsRef });
    const computedClassName = cls(['tab'], style, classNames);
    const tabActiveClassName = isActive && classNames.active || '';

    return (
        <AccessibilityElement
            onClick={onClick}
            className={`${className} ${computedClassName} ${tabActiveClassName}`.trim()}
        >
            {children}
        </AccessibilityElement>
    );
};

export const Tabs = ({ className = '', classNames, children, onChange, activeTab }) => {
    const tabsRef = useRef(null);
    const tabsClassName = cls(['tabs'], style, classNames || {});

    return (
        <TabsContext.Provider value={{ onChange, activeTab, classNames, tabsRef }}>
            <div className={`${style.container} ${className}`}>
                <div ref={tabsRef} className={tabsClassName}>
                    {children}
                </div>
            </div>
        </TabsContext.Provider>
    );
};

export default Tabs;
